import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { CompanySwitcher } from './CompanySwitcher';
import { useThemeContext } from '../../contexts/ThemeContext';
import { Button } from '../ui/button';

const LANGUAGES = [
  { code: 'en', label: 'EN' },
  { code: 'th', label: 'TH' },
  { code: 'zh', label: 'ZH' },
];

function SunIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="4" />
      <line x1="12" y1="2" x2="12" y2="6" />
      <line x1="12" y1="18" x2="12" y2="22" />
      <line x1="4.93" y1="4.93" x2="7.76" y2="7.76" />
      <line x1="16.24" y1="16.24" x2="19.07" y2="19.07" />
      <line x1="2" y1="12" x2="6" y2="12" />
      <line x1="18" y1="12" x2="22" y2="12" />
      <line x1="4.93" y1="19.07" x2="7.76" y2="16.24" />
      <line x1="16.24" y1="7.76" x2="19.07" y2="4.93" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 12.79A9 9 0 1 1 11.21 3a7 7 0 0 0 9.79 9.79z" />
    </svg>
  );
}

export function TopBar() {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const { isDark, toggleTheme } = useThemeContext();

  const handleLanguageChange = (code: string) => {
    i18n.changeLanguage(code);
  };

  return (
    <header className="flex h-14 items-center justify-between border-b border-blue-800 bg-blue-900 px-6 dark:border-gray-800 dark:bg-gray-950">
      <div className="text-sm text-blue-100 dark:text-gray-300">
        {user && (
          <span>
            {user.displayName}{' '}
            <span className="text-xs text-blue-300 dark:text-gray-500">
              ({t(`roles.${user.role}`)})
            </span>
          </span>
        )}
      </div>

      <div className="flex items-center gap-3">
        {/* Company switcher — admin only */}
        {user?.role === 'admin' && <CompanySwitcher />}

        {/* Dark mode toggle */}
        <button
          onClick={toggleTheme}
          title={isDark ? t('common.lightMode') : t('common.darkMode')}
          aria-label={isDark ? t('common.lightMode') : t('common.darkMode')}
          className="flex h-8 w-8 items-center justify-center rounded-md border border-blue-700 text-blue-300 transition-colors hover:bg-blue-800 hover:text-white dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100"
        >
          {isDark ? <SunIcon /> : <MoonIcon />}
        </button>

        {/* Language switcher */}
        <div className="flex rounded-md border border-blue-700 dark:border-gray-600">
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className={`px-2 py-1 text-xs font-medium transition-colors ${
                i18n.language === lang.code
                  ? 'bg-primary text-white'
                  : 'text-blue-300 hover:bg-blue-800 hover:text-white dark:text-gray-400 dark:hover:bg-gray-700'
              } ${lang.code === 'en' ? 'rounded-l-md' : ''} ${
                lang.code === 'zh' ? 'rounded-r-md' : ''
              }`}
            >
              {lang.label}
            </button>
          ))}
        </div>

        {/* Logout */}
        <Button variant="ghost" size="sm" onClick={logout}>
          {t('auth.logout')}
        </Button>
      </div>
    </header>
  );
}
